import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Fade, IconButton, Modal, Typography } from '@mui/material';
import Button from 'components/Button/Button';
import { CloseRounded } from '@mui/icons-material';
import { docGeniusPalette } from 'styles/partials/colors';
import { useGetEnvelopeInfoQuery } from 'features/docuSign/docuSignApi';
import { formatToTitleCase } from 'utils/formatDocumentType';

import moment from 'moment';
import styles from './DetailsModal.module.css';
interface SendiFileModalProps {
  open: boolean;
  documentInfo: {
    id: number | null;
    documentName: string;
  };
  onClose: () => void;
}

const DetailsModal = ({ documentInfo, open, onClose }: SendiFileModalProps) => {
  const { id, documentName } = documentInfo;
  const [modalInfo, setModalInfo] = useState<{
    status: string;
    createdOn: string;
    sentOn: string;
    signedOn?: string;
    recipients: string[];
  }>({
    status: '',
    createdOn: '',
    sentOn: '',
    recipients: [],
  });

  const { data, isLoading } = useGetEnvelopeInfoQuery(Number(id), {
    skip: id === null,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (data) {
      setModalInfo({
        status: data.data.status,
        createdOn: data.data.createdOn,
        sentOn: data.data.sentOn,
        recipients: [],
      });
    }
  }, [data]);

  const handleOnClose = () => {
    onClose();
  };

  const formatDateString = (dateString: string) => {
    return moment(dateString).format('MM/DD/YYYY [at] h:mm:ss a');
  };

  const formatTitle = (value: string) => {
    if (value) {
      return formatToTitleCase(value);
    }
    return value;
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Fade in={open}>
        <Box className={styles.modalContainer}>
          <Box className={styles.modalHeader}>
            {' '}
            <Typography component={'h1'} fontSize={'24px'} fontWeight={'500'} color={'#474747'} marginBottom={'32px'}>
              Built With DocuSign: {documentName}
            </Typography>
            <IconButton aria-label='close-modal' onClick={handleOnClose}>
              <CloseRounded sx={{ color: docGeniusPalette.primaryBlue }} />
            </IconButton>
          </Box>
          <Box className={styles.modalDescription}>
            <p>Here&apos;s a list of details about your built file:</p>
          </Box>
          {isLoading ? (
            <Box className={styles.Layout}>
              <CircularProgress />
            </Box>
          ) : (
            <Box className={styles.modalBody}>
              <Box className={styles.column}>
                <Box>
                  <p className={styles.infoTitle}>Status</p>
                  <p className={styles.info}>{formatTitle(modalInfo.status)}</p>
                </Box>
                <Box>
                  <p className={styles.infoTitle}>Recipients</p>
                  <p className={styles.info}>
                    {modalInfo.recipients.map((fullName, index) =>
                      index + 1 !== modalInfo.recipients.length ? `${fullName},` : `${fullName}`
                    )}
                  </p>
                </Box>
              </Box>
              <Box className={styles.column}>
                <Box>
                  <p className={styles.infoTitle}>Created On</p>
                  <p className={styles.info}>{formatDateString(modalInfo.createdOn)}</p>
                </Box>
                <Box>
                  <p className={styles.infoTitle}>Sent On</p>
                  <p className={styles.info}>{formatDateString(modalInfo.sentOn)}</p>
                </Box>
                <Box>
                  <p className={styles.infoTitle}>Signed On</p>
                  {modalInfo.signedOn && <p className={styles.info}>{formatDateString(modalInfo.signedOn)}</p>}
                </Box>
              </Box>
            </Box>
          )}
          <Box className={styles.modalFooter}>
            <Button className={styles.secondaryBtn} variant='secondary' onClick={handleOnClose}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default DetailsModal;
